import { isEmpty } from 'lodash-es';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReplaySubject, type Observable } from 'rxjs';
import styled from 'styled-components';
import { useConstant } from '../../../hooks/useConstant';
import { UpdateActionEnum, type MinimalSubscriptionResponse } from '../../../types';
import {
  DEFAULT_MAX_ROWS,
  removeEmptyFilters,
  useBlotterTable,
  useRestBlotterTable,
  useWsBlotterTable,
  type BlotterTableFilter,
  type InitialFetchState,
  type UseBlotterTableProps,
  type WebsocketRequest,
} from '../../BlotterTable';
import { Panel } from '../../Panel';
import { RR6Prompt } from '../../Prompt';
import {
  EntityAdminBlotter,
  EntityAdminDrawer,
  EntityAdminHeader,
  EntityAdminTabsAndFilters,
  useEntityAdminHeaderLayout,
} from '../components';
import { EntityAdminBulkUploadDialog } from '../components/EntityAdminBulkUploadDialog';
import { EntityAdminConfirmationDialog } from '../components/EntityAdminConfirmationDialog';
import { useIsInsideEntityAdminDrawer } from '../components/EntityAdminDrawerContext';
import type { EntityAdminClass } from '../EntityAdminClass';
import type {
  EntityAdminContentProps,
  EntityAdminProps,
  EntityAdminRecord,
  EntityAdminRESTProps,
  EntityAdminWSProps,
} from '../types';
import { useEntityAdmin } from '../useEntityAdmin';
import { useEntityAdminBlotterFilter } from '../useEntityAdminBlotterFilter';
import { useEntityAdminBlotterProps } from '../useEntityAdminBlotterProps';
import { hasAnyAddAllowed, isWaitingForServerFilterProperties } from '../utils';
import { EntityAdminTabsWrapper, useEntityAdminTabsContext } from '../wrappers/EntityAdminTabsWrapper';

/**
 * EntityAdmin root fills its parent without page-level scrolling so AG Grid can size
 * correctly when nested in Dockview splits (or any constrained flex parent).
 */
const EntityAdminRoot = styled(Panel)`
  overflow: hidden;
  min-height: 0;
`;

function getRecordForBlotterUpdate<TRecord extends EntityAdminRecord>(
  record: TRecord,
  action: 'add' | 'update' | 'remove'
) {
  if (action === 'remove') {
    return { ...record, UpdateAction: UpdateActionEnum.Remove };
  }
  return record;
}

/** Bulk upload matches CSV rows against loaded entities, so it needs every row loaded and no server filter. */
function useBulkUploadReady({
  setIsBulkUploadReady,
  initialFetchState,
  serverFilter,
  paginationLimit,
}: {
  setIsBulkUploadReady: (isReady: boolean) => void;
  initialFetchState: InitialFetchState;
  serverFilter: BlotterTableFilter | undefined;
  paginationLimit?: { limitReached?: boolean; isLoading?: boolean };
}) {
  const isReady =
    initialFetchState.isComplete &&
    !paginationLimit?.limitReached &&
    !paginationLimit?.isLoading &&
    isEmpty(removeEmptyFilters(serverFilter));
  useEffect(() => setIsBulkUploadReady(isReady), [isReady, setIsBulkUploadReady]);
}

export const EntityAdmin = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord = TRecord>(
  props: EntityAdminProps<TRecord, TDrawerRecord>
) => {
  if (props.drawerType === 'custom' && props.customDrawerLayout == null) {
    throw new Error('customDrawerLayout is required when drawerType is custom');
  }

  if (props.allowBulkSelect === true && props.allowBulkEdit === true) {
    throw new Error('allowBulkSelect and allowBulkEdit cannot both be enabled');
  }

  if (props.allowBulkUpload === true && props.suppressLoadingUntilFetched === true) {
    throw new Error('allowBulkUpload and suppressLoadingUntilFetched cannot both be enabled');
  }

  const entityAdminProps = useEntityAdmin<TRecord, TDrawerRecord>({
    ...props,
    renderDrawer: entityDrawer => (
      <EntityAdminDrawer<TRecord, TDrawerRecord>
        {...entityDrawer}
        customDrawerLayout={props.customDrawerLayout}
        drawerType={props.drawerType}
        allowAddEntity={props.allowAddEntity}
        allowEditEntity={props.allowEditEntity}
        allowDeleteEntity={props.allowDeleteEntity}
        newEntityDefault={props.newEntityDefault}
        drawerContentSuffix={props.drawerContentSuffix}
      />
    ),
  });

  if (
    (hasAnyAddAllowed(props.allowAddEntity) ||
      props.allowEditEntity ||
      props.allowDeleteEntity ||
      props.allowBulkUpload) &&
    props.path == null
  ) {
    throw new Error(
      'path is required when allowAddEntity, allowEditEntity, allowDeleteEntity, or allowBulkUpload is true'
    );
  }

  // Dirty-leave prompt on pages only — skip spaces and nested admins.
  const isInsideEntityAdminDrawer = useIsInsideEntityAdminDrawer();
  const { isPanelLayout } = useEntityAdminHeaderLayout();
  const shouldBlockNavigation =
    !isPanelLayout &&
    !isInsideEntityAdminDrawer &&
    entityAdminProps.isOwnedEntityAdminDrawerOpen &&
    entityAdminProps.isEntityAdminDrawerDirty;

  const EntityAdminContentPanel = useMemo(
    () => (
      <EntityAdminRoot background="colors.gray.000" gap="1px" minHeight={0}>
        {shouldBlockNavigation && (
          <RR6Prompt
            when
            message="You have unsaved changes. Are you sure you want to leave?"
            handleOnNavigated={entityAdminProps.closeEntityAdminDrawer}
          />
        )}
        <EntityAdminConfirmationDialog
          context={entityAdminProps.confirmDialog.context}
          isOpen={entityAdminProps.confirmDialog.isOpen}
          close={entityAdminProps.confirmDialog.close}
        />
        {entityAdminProps.allowBulkUpload ? (
          <EntityAdminBulkUploadDialog<TRecord, TDrawerRecord>
            isOpen={entityAdminProps.bulkUploadDialog.isOpen}
            close={entityAdminProps.bulkUploadDialog.close}
            entityName={entityAdminProps.entityName}
            getBulkUploadParams={entityAdminProps.getBulkUploadParams}
            onImport={entityAdminProps.bulkUploadEntities}
          />
        ) : null}
        {entityAdminProps.suppressHeader ? null : <EntityAdminHeader {...entityAdminProps} />}
        {entityAdminProps.pageSubHead}
        <EntityAdminTabsAndFilters filtersPortalId={entityAdminProps.filtersPortalId} />
        {'wsRequest' in entityAdminProps && entityAdminProps.wsRequest != null ? (
          <EntityAdminWSContent<TRecord, TDrawerRecord> {...entityAdminProps} wsRequest={entityAdminProps.wsRequest} />
        ) : entityAdminProps.path != null && entityAdminProps.dataObservable == null ? (
          <EntityAdminRESTContent<TRecord, TDrawerRecord> {...entityAdminProps} path={entityAdminProps.path} />
        ) : entityAdminProps.dataObservable != null ? (
          <EntityAdminObservableContent<TRecord, TDrawerRecord>
            {...entityAdminProps}
            dataObservable={entityAdminProps.dataObservable}
          />
        ) : null}
      </EntityAdminRoot>
    ),
    [entityAdminProps, shouldBlockNavigation]
  );

  const entityAdminWrappers: Array<(children: React.ReactNode) => JSX.Element> = [];

  /**
   * EntityAdminTabsWrapper
   * Manages the tab items and the tabs context.
   * Due to the way this wrapper works (relies on array of EntityAdmin children), it must be the first wrapper in the list.
   */
  const existingTabsContext = useEntityAdminTabsContext();
  if (entityAdminProps.useTabs) {
    /**
     * It is possible that the EntityAdminTabsWrapper is already present in the tree.
     * This is the case for /dealer/tiers for example, where the tabs context is shared between two pages.
     */
    if (existingTabsContext != null) {
      throw new Error('EntityAdminTabsContext is already present in the tree.');
    }
    entityAdminWrappers.push(children => (
      <EntityAdminTabsWrapper persistKey={`${props.persistKey}/tabs`} allowAddTabs={true}>
        {children}
      </EntityAdminTabsWrapper>
    ));
  }

  /** If more wrappers are added in the future, they should be added here.
   * entityAdminWrappers.push(children => <Wrapper>{children}</Wrapper>);
   */

  return entityAdminWrappers.reduce((content, wrap) => wrap(content), EntityAdminContentPanel);
};

const EntityAdminWSContent = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord = TRecord>(
  props: EntityAdminWSProps<TRecord, TDrawerRecord> & EntityAdminContentProps<TRecord, TDrawerRecord>
) => {
  const { onBlotterTableApiReady } = props;
  const { blotterTableProps, sharedFiltersProps, openFilterClauseRef } = useEntityAdminBlotterProps<
    TRecord,
    TDrawerRecord
  >(props);

  const wsRequestWithServerFilter = useMemo(
    () =>
      ({
        ...props.wsRequest,
        ...removeEmptyFilters(blotterTableProps.filter),
      }) as WebsocketRequest,
    [props.wsRequest, blotterTableProps.filter]
  );
  const waitingForServerFilter = isWaitingForServerFilterProperties(
    props.filterableServerFields,
    props.filterableProperties,
    sharedFiltersProps.filterResults.filter
  );

  const blotterTableWS = useWsBlotterTable<WebsocketRequest, EntityAdminClass<TRecord>, TRecord>({
    request: waitingForServerFilter ? null : wsRequestWithServerFilter,
    waitForAllPages: !props.suppressLoadingUntilFetched,
    ...blotterTableProps,
  });
  useBulkUploadReady({
    setIsBulkUploadReady: props.setIsBulkUploadReady,
    initialFetchState: blotterTableWS.initialFetchState,
    serverFilter: blotterTableProps.filter,
    paginationLimit: blotterTableWS.paginationLimit,
  });

  const blotterTableFilter = useEntityAdminBlotterFilter<TRecord, TDrawerRecord>({
    ...sharedFiltersProps,
    exportOptions: blotterTableWS.exportOptions,
    exportDataAsCSV: blotterTableWS.exportDataAsCSV,
    exportDataAsExcel: blotterTableWS.exportDataAsExcel,
    blotterTableFiltersProps: {
      ...blotterTableWS.blotterTableFiltersProps,
      ...props.blotterTableFiltersProps,
    },
    collapseAllGroups: blotterTableWS.collapseAllGroups,
    expandAllGroups: blotterTableWS.expandAllGroups,
    gridApi: blotterTableWS.gridApi,
    openFilterClauseRef,
  });

  useEffect(() => {
    if (blotterTableWS.gridApi != null) {
      props.blotterTableApiRef.current.gridApi = blotterTableWS.gridApi;
      props.blotterTableApiRef.current.ensureRowVisibleById = blotterTableWS.ensureRowVisibleById;
      props.blotterTableApiRef.current.updateRecord = (record, action) => {
        // For EntityAdmin with WS, apply the transaction optimistically so that selected rows update immediately.
        blotterTableWS.updateRecord(getRecordForBlotterUpdate(record, action));
      };
      onBlotterTableApiReady?.(props.blotterTableApiRef.current);
    }
  }, [blotterTableWS, props.blotterTableApiRef, onBlotterTableApiReady]);

  return (
    <EntityAdminBlotter<TRecord>
      blotterTable={blotterTableWS}
      blotterTableFilter={blotterTableFilter}
      blotterTableHidden={props.blotterTableHidden}
      blotterTableOverlay={props.blotterTableOverlay}
      blotterTableFooter={props.blotterTableFooter}
    />
  );
};

const EntityAdminRESTContent = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord = TRecord>({
  blotterRowCount = DEFAULT_MAX_ROWS,
  paginationSize,
  ...props
}: EntityAdminRESTProps<TRecord, TDrawerRecord> & EntityAdminContentProps<TRecord, TDrawerRecord>) => {
  const { onBlotterTableApiReady } = props;
  const { blotterTableProps, sharedFiltersProps, openFilterClauseRef } = useEntityAdminBlotterProps<
    TRecord,
    TDrawerRecord
  >(props);

  const waitingForServerFilter = isWaitingForServerFilterProperties(
    props.filterableServerFields,
    props.filterableProperties,
    sharedFiltersProps.filterResults.filter
  );

  const blotterTableREST = useRestBlotterTable<EntityAdminClass<TRecord>, TRecord>({
    // "limit" here is misleading - it's actually the number of records to fetch at a time
    request: waitingForServerFilter
      ? null
      : {
          baseUrl: props.baseUrl,
          path: props.path,
          // Use Math.min to prevent fetching more records per request than our total display limit.
          // Without this, if paginationSize > blotterRowCount, we'd over-fetch data and create
          // inconsistencies between what we fetch vs. what we display/paginate.
          limit: paginationSize ? Math.min(paginationSize, blotterRowCount) : undefined,
        },
    // "blotterRowCount" is the number of records we will fetch until, and then we stop even if there is a "next".
    blotterRowCount,
    queryParams: props.queryParams,
    suppressLoadingUntilFetched: props.suppressLoadingUntilFetched,
    ...blotterTableProps,
  });
  useBulkUploadReady({
    setIsBulkUploadReady: props.setIsBulkUploadReady,
    initialFetchState: blotterTableREST.initialFetchState,
    serverFilter: blotterTableProps.filter,
    paginationLimit: blotterTableREST.paginationLimit,
  });

  const blotterTableFilter = useEntityAdminBlotterFilter<TRecord, TDrawerRecord>({
    ...sharedFiltersProps,
    exportOptions: blotterTableREST.exportOptions,
    exportDataAsCSV: blotterTableREST.exportDataAsCSV,
    exportDataAsExcel: blotterTableREST.exportDataAsExcel,
    blotterTableFiltersProps: {
      ...blotterTableREST.blotterTableFiltersProps,
      ...props.blotterTableFiltersProps,
    },
    collapseAllGroups: blotterTableREST.collapseAllGroups,
    expandAllGroups: blotterTableREST.expandAllGroups,
    gridApi: blotterTableREST.gridApi,
    openFilterClauseRef,
  });

  useEffect(() => {
    if (blotterTableREST.gridApi != null) {
      props.blotterTableApiRef.current.gridApi = blotterTableREST.gridApi;
      props.blotterTableApiRef.current.refresh = blotterTableREST.refresh;
      props.blotterTableApiRef.current.ensureRowVisibleById = blotterTableREST.ensureRowVisibleById;
      props.blotterTableApiRef.current.updateRecord = (record, action) => {
        // For EntityAdmin with REST, apply the transaction by updating the data subject in useRestBlotterTable.
        blotterTableREST.updateRecord(getRecordForBlotterUpdate(record, action));
      };
      onBlotterTableApiReady?.(props.blotterTableApiRef.current);
    }
  }, [blotterTableREST, props.blotterTableApiRef, onBlotterTableApiReady]);

  return (
    <EntityAdminBlotter<TRecord>
      blotterTable={blotterTableREST}
      blotterTableFilter={blotterTableFilter}
      paginationLimit={blotterTableREST.paginationLimit}
      blotterTableHidden={props.blotterTableHidden}
      blotterTableOverlay={props.blotterTableOverlay}
      blotterTableFooter={props.blotterTableFooter}
    />
  );
};

const EntityAdminObservableContent = <
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord = TRecord,
>(
  props: EntityAdminContentProps<TRecord, TDrawerRecord> & {
    dataObservable: Observable<TRecord[]>;
    isDataLoading?: boolean;
  }
) => {
  const { onBlotterTableApiReady } = props;
  const { blotterTableProps, sharedFiltersProps, openFilterClauseRef } = useEntityAdminBlotterProps<
    TRecord,
    TDrawerRecord
  >(props);

  const dataSubject = useConstant(new ReplaySubject<MinimalSubscriptionResponse<TRecord>>(1));
  const bufferedData = useRef<TRecord[] | null>(null);
  const [observableFetchState, setObservableFetchState] = useState<InitialFetchState>({
    isComplete: false,
    loadedCount: 0,
  });

  const shouldBuffer = !props.suppressLoadingUntilFetched && props.isDataLoading != null;

  // Read via ref inside the subscription so toggling isDataLoading doesn't tear down/resubscribe to
  // dataObservable (which can be a shared, refCounted observable that loses accumulated state on resubscribe).
  const isDataLoadingRef = useRef(props.isDataLoading);
  isDataLoadingRef.current = props.isDataLoading;

  useEffect(() => {
    bufferedData.current = null;
    const subscription = props.dataObservable.subscribe(data => {
      if (shouldBuffer && isDataLoadingRef.current) {
        bufferedData.current = data;
        setObservableFetchState({ isComplete: false, loadedCount: data.length });
      } else {
        bufferedData.current = null;
        setObservableFetchState({ isComplete: true, loadedCount: data.length });
        dataSubject.next({ data, initial: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [dataSubject, props.dataObservable, shouldBuffer]);

  // When isDataLoading transitions to false, flush buffered data to the grid
  useEffect(() => {
    if (!props.isDataLoading && bufferedData.current != null) {
      const data = bufferedData.current;
      bufferedData.current = null;
      setObservableFetchState({ isComplete: true, loadedCount: data.length });
      dataSubject.next({ data, initial: true });
    }
  }, [props.isDataLoading, dataSubject]);

  const pipedDataObservable: Observable<MinimalSubscriptionResponse<EntityAdminClass<TRecord>>> = useMemo(
    () => dataSubject.pipe(blotterTableProps.pipe),
    [dataSubject, blotterTableProps.pipe]
  );

  const blotterTable = useBlotterTable<EntityAdminClass<TRecord>>({
    dataObservable: pipedDataObservable,
    ...blotterTableProps,
  } as UseBlotterTableProps<EntityAdminClass<TRecord>>);
  useBulkUploadReady({
    setIsBulkUploadReady: props.setIsBulkUploadReady,
    initialFetchState: observableFetchState,
    serverFilter: blotterTableProps.filter,
  });

  const blotterTableFilter = useEntityAdminBlotterFilter<TRecord, TDrawerRecord>({
    ...sharedFiltersProps,
    exportOptions: blotterTable.exportOptions,
    exportDataAsCSV: blotterTable.exportDataAsCSV,
    exportDataAsExcel: blotterTable.exportDataAsExcel,
    blotterTableFiltersProps: {
      ...blotterTable.blotterTableFiltersProps,
      ...props.blotterTableFiltersProps,
    },
    collapseAllGroups: blotterTable.collapseAllGroups,
    expandAllGroups: blotterTable.expandAllGroups,
    gridApi: blotterTable.gridApi,
    openFilterClauseRef,
  });

  useEffect(() => {
    if (blotterTable.gridApi != null) {
      props.blotterTableApiRef.current.gridApi = blotterTable.gridApi;
      props.blotterTableApiRef.current.ensureRowVisibleById = blotterTable.ensureRowVisibleById;
      props.blotterTableApiRef.current.updateRecord = (record, action) => {
        dataSubject.next({
          data: [getRecordForBlotterUpdate(record, action)],
        });
      };
      onBlotterTableApiReady?.(props.blotterTableApiRef.current);
    }
  }, [
    blotterTable.gridApi,
    blotterTable.ensureRowVisibleById,
    dataSubject,
    props.blotterTableApiRef,
    onBlotterTableApiReady,
  ]);

  return (
    <EntityAdminBlotter<TRecord>
      blotterTable={{ ...blotterTable, initialFetchState: observableFetchState }}
      blotterTableFilter={blotterTableFilter}
      blotterTableHidden={props.blotterTableHidden}
      blotterTableOverlay={props.blotterTableOverlay}
      blotterTableFooter={props.blotterTableFooter}
    />
  );
};
